import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { getQuestionById } from "../../../api/questionApis";
import { getFileType } from "../../../utils/helper";
import "react-toastify/dist/ReactToastify.css";
import "./QuestionDetail.css";
import PdfReader from "../../../components/PdfReader";

const placeholderImg = "https://placehold.co/600x400?text=No+attachment";

export default function QuestionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);

  useEffect(() => {
    const fetchQuestion = async () => {
      setLoading(true);
      try {
        const res = await getQuestionById(id);
        if (res?.data?.data) setQuestion(res.data.data);
        else toast.error("Question not found");
      } catch (err) {
        toast.error(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };
    fetchQuestion();
  }, [id]);

  if (loading)
    return (
      <div className="question-detail-wrapper">
        <div className="loading">Loading...</div>
      </div>
    );

  if (!question)
    return (
      <div className="question-detail-wrapper">
        <div className="loading">No question found.</div>
      </div>
    );

  const questionImage = question.question_file || placeholderImg;

  // Scroll carousel by the width of the container
  const scrollDependencies = (direction) => {
    const container = document.querySelector(
      ".dependencies-thumbnails-container",
    );
    if (container) {
      const scrollAmount = container.clientWidth;
      container.scrollBy({
        left: direction === "next" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="question-detail-wrapper">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Fullscreen Modal */}
      {fullscreenImage && (
        <div
          className="fullscreen-overlay"
          onClick={() => setFullscreenImage(null)}
        >
          <button
            className="close-icon"
            onClick={() => setFullscreenImage(null)}
          >
            ×
          </button>
          <div className="fullscreen-image-container">
            {getFileType(fullscreenImage) === "pdf" ? (
              <PdfReader file={fullscreenImage} />
            ) : (
              <img
                className="fullscreen-img"
                src={fullscreenImage}
                alt="Fullscreen"
                onError={(e) => (e.target.src = placeholderImg)}
              />
            )}
          </div>
        </div>
      )}

      <div className="detail-container">
        <div className="detail-top">
          <button
            className="back-btn"
            variant="primary"
            onClick={() => navigate(-1)}
          >
            ← Back to Questions
          </button>
          <h2 className="detail-header">Question Detail</h2>
        </div>
        <div className="detail-content">
          {/* Left Section: Images */}
          <div className="image-section">
            <div className="image-container">
              <h3>Question</h3>
              {getFileType(questionImage) === "pdf" ? (
                <PdfReader file={questionImage} />
              ) : (
                <img
                  src={questionImage}
                  alt={question.paper || "Question"}
                  className="clickable-img"
                  onClick={() => setFullscreenImage(questionImage)}
                  onError={(e) => (e.target.src = placeholderImg)}
                />
              )}
            </div>

            {question.ms_file && (
              <div className="image-container ms-file mt-4">
                <h3>Marking Scheme</h3>
                {getFileType(question.ms_file) === "pdf" ? (
                  <PdfReader file={question.ms_file} />
                ) : (
                  <img
                    src={question.ms_file}
                    alt="MS File"
                    className="clickable-img"
                    onClick={() => setFullscreenImage(question.ms_file)}
                    onError={(e) => (e.target.src = placeholderImg)}
                  />
                )}
              </div>
            )}

            {question.solution_file && (
              <div className="image-container solution-file mt-4">
                <h3>Solution</h3>
                {getFileType(question.solution_file) === "pdf" ? (
                  <PdfReader file={question.solution_file} />
                ) : (
                  <img
                    src={question.solution_file}
                    alt="Solution File"
                    className="clickable-img"
                    onClick={() => setFullscreenImage(question.solution_file)}
                    onError={(e) => (e.target.src = placeholderImg)}
                  />
                )}
              </div>
            )}

            {/* Dependencies Carousel */}
            {question.dependencies && question.dependencies.length > 0 && (
              <div className="dependencies-container">
                <strong>Dependencies:</strong>

                <div className="carousel-wrapper">
                  {/* Only show prev button if more than 2 dependencies */}
                  {question.dependencies.length > 2 && (
                    <button
                      className="carousel-btn prev-btn"
                      onClick={() => scrollDependencies("prev")}
                    >
                      ‹
                    </button>
                  )}

                  <div className="dependencies-thumbnails-container">
                    {question.dependencies.map((dep, idx) => (
                      <div key={idx} className="dependency-wrapper">
                        {getFileType(dep.file_name) === "pdf" ? (
                          <div className="dependency-pdf">
                            <PdfReader file={dep.file_name} />
                          </div>
                        ) : (
                          <img
                            src={dep.file_name}
                            alt={`Dependency ${idx + 1}`}
                            className="dependency-img clickable-img"
                            onClick={() => setFullscreenImage(dep.file_name)}
                            onError={(e) => (e.target.src = placeholderImg)}
                          />
                        )}
                        <div className="dependency-info">
                          <p>
                            <strong>Paper:</strong> {dep.paper || "N/A"}
                          </p>
                          <p>
                            <strong>Year:</strong> {dep.year || "N/A"}
                          </p>
                          <p>
                            <strong>Part:</strong> {dep.part || "N/A"}
                          </p>
                          <p>
                            <strong>Chapter:</strong>{" "}
                            {Array.isArray(dep.chapter)
                              ? dep.chapter.map((c) => c?.title || c).join(", ")
                              : dep.chapter?.title || "N/A"}
                          </p>
                          <p>
                            <strong>Section:</strong>{" "}
                            {Array.isArray(dep.section)
                              ? dep.section.map((s) => s?.title || s).join(", ")
                              : dep.section?.title || "N/A"}
                          </p>
                          <p>
                            <strong>Is Context:</strong>{" "}
                            {dep.is_context ? "Yes" : "No"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Only show next button if more than 2 dependencies */}
                  {question.dependencies.length > 2 && (
                    <button
                      className="carousel-btn next-btn"
                      onClick={() => scrollDependencies("next")}
                    >
                      ›
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Section: Info */}
          <div className="info-container">
            <p>
              <strong>Paper:</strong> {question.paper || "N/A"}
            </p>
            <p>
              <strong>Year:</strong> {question.year || "N/A"}
            </p>
            <p>
              <strong>Question Number:</strong>{" "}
              {question.question_number || "N/A"}
            </p>
            <p>
              <strong>Part:</strong> {question.part || "N/A"}
            </p>
            <p>
              <strong>Subpart:</strong> {question.subpart || "N/A"}
            </p>
            <p>
              <strong>Chapter:</strong>{" "}
              {Array.isArray(question?.chapter) && question.chapter.length > 0
                ? question.chapter.map((c) => c?.title || c).join(", ")
                : question.chapter?.title || "N/A"}
            </p>
            <p>
              <strong>Section:</strong>{" "}
              {Array.isArray(question?.section) && question.section.length > 0
                ? question.section.map((s) => s?.title || s).join(", ")
                : question.section?.title || "N/A"}
            </p>
            <p>
              <strong>Marks:</strong> {question.marks || "N/A"}
            </p>
            <p>
              <strong>Order:</strong> {question.order || "N/A"}
            </p>
            <p>
              <strong>Topics:</strong>{" "}
              {question.topics?.map((t) => t.title).join(", ") || "N/A"}
            </p>
            <p>
              <strong>Subtopics:</strong>{" "}
              {question.subtopics?.map((s) => s.title).join(", ") || "N/A"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
